import type { ColorSystem } from '@mui/material/styles';

import { setFont, hexToRgbChannel, createPaletteChannel } from 'minimal-shared/utils';

import type { ThemeOptions, ThemeColorScheme, ThemeSettings } from '../types';

import { createShadowColor } from '../core/custom-shadows';

// ----------------------------------------------------------------------

/**
 * Update the core theme with the settings state.
 * @contrast
 * @primaryColor
 */

export function updateCoreWithSettings(
  theme: ThemeOptions,
  themeSettings?: ThemeSettings
): ThemeOptions {
  const {
    direction,
    fontFamily,
    contrast = 'default',
    primaryColor,
    secondaryColor: secondadyColor,
  } = themeSettings ?? {};

  const isDefaultContrast = contrast === 'default';

  const lightPalette = theme.colorSchemes?.light.palette as ColorSystem['palette'];

  const updatedPrimaryColor = createPaletteChannel(primaryColor!);
  const updatedSecondaryColor = createPaletteChannel(secondadyColor!);

  const updateColorScheme = (scheme: ThemeColorScheme) => {
    const colorSchemes = theme.colorSchemes?.[scheme];

    const updatedPalette = {
      ...colorSchemes?.palette,
      ...{
        primary: updatedPrimaryColor,
        secondary: updatedSecondaryColor,
      },
      ...(scheme === 'light' && {
        background: {
          ...lightPalette?.background,
          ...(!isDefaultContrast && {
            default: lightPalette.grey[200],
            defaultChannel: hexToRgbChannel(lightPalette.grey[200]),
          }),
        },
      }),
    };

    const updatedCustomShadows = {
      ...colorSchemes?.customShadows,
      ...{
        primary: createShadowColor(updatedPrimaryColor.mainChannel),
        secondary: createShadowColor(updatedSecondaryColor.mainChannel),
      },
    };

    return {
      ...colorSchemes,
      palette: updatedPalette,
      customShadows: updatedCustomShadows,
    };
  };

  return {
    ...theme,
    direction,
    colorSchemes: {
      light: updateColorScheme('light'),
      dark: updateColorScheme('dark'),
    },
    typography: {
      ...theme.typography,
      fontFamily: setFont(fontFamily?.primary),
    },
  };
}
